import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  createLinkToken,
  fetchStatus,
  recoverSubmit,
  refreshAfterTerminal,
  skip as skipPostApprovalPlaid,
  submit,
} from '../actions/post_approval_plaid';
import {
  POST_APPROVAL_PLAID_IDEMPOTENCY_KEY_SET,
  POST_APPROVAL_PLAID_RESET,
} from '../lib/constants/ActionTypes';
import { newIdempotencyKey } from '../lib/utils/post_approval_plaid';
import {
  nextDelayMs,
  phase as pollingPhase,
  shouldAutoRetry,
} from '../lib/utils/post_approval_plaid_polling';
import { capturePostApprovalPlaidEntitlementBaseline } from './postApprovalPlaidEntitlementBaseline';

const SLOW_RETRY_DELAY_MS = 5000;
const isAmbiguousSubmit = (error) =>
  error.network ||
  error.reason_code === 'invalid_response' ||
  error.http_status >= 500;

const usePostApprovalPlaid = ({ active = true } = {}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const session = useSelector((state) => state.sessions.get('session'));
  const publicKey = useSelector((state) => state.sessions.get('public_key'));
  const plaid = useSelector((state) => state.post_approval_plaid);
  const applicationId = session.get('application_id');
  const sessionId = session.get('id');
  const attempt = plaid.get('attempt');
  const loading = plaid.get('loading');
  const error = plaid.get('last_error');
  const idempotencyKey = plaid.get('idempotency_key');
  const submitInFlight = useRef(null);
  const pollAttempt = useRef(1);
  const slowRetries = useRef(0);
  const consecutiveFailures = useRef(0);
  const lastPollError = useRef(null);
  const pollingAttemptId = useRef(null);
  const mounted = useRef(false);
  const terminalKey = attempt.isTerminal
    ? JSON.stringify([
        sessionId,
        publicKey,
        attempt.get('attempt_id'),
        attempt.get('status'),
      ])
    : null;
  const terminalKeyRef = useRef(terminalKey);
  terminalKeyRef.current = terminalKey;
  const [entityRefresh, setEntityRefresh] = useState({
    key: null,
    status: 'idle',
  });
  const [pollingExhausted, setPollingExhausted] = useState(false);

  const refreshStatus = useCallback(
    () =>
      applicationId &&
      dispatch(fetchStatus(applicationId, { sessionId, publicKey })),
    [applicationId, dispatch, publicKey, sessionId]
  );

  const refreshEntities = useCallback(() => {
    if (!terminalKey) return Promise.resolve(null);

    capturePostApprovalPlaidEntitlementBaseline(store);
    setEntityRefresh({ key: terminalKey, status: 'refreshing' });
    return dispatch(refreshAfterTerminal()).then(
      (result) => {
        if (mounted.current && terminalKeyRef.current === terminalKey) {
          setEntityRefresh(
            result
              ? { key: terminalKey, status: 'ready' }
              : { key: null, status: 'idle' }
          );
        }
        return result;
      },
      (refreshError) => {
        if (mounted.current && terminalKeyRef.current === terminalKey) {
          setEntityRefresh({
            key: terminalKey,
            status: 'error',
            error: refreshError,
          });
        }
        throw refreshError;
      }
    );
  }, [dispatch, store, terminalKey]);

  const openLink = useCallback(
    () =>
      applicationId &&
      dispatch(createLinkToken(applicationId, { sessionId, publicKey })),
    [applicationId, dispatch, publicKey, sessionId]
  );

  const submitSelectedAccount = useCallback(
    (publicToken, account) => {
      const activeSessions = store.getState().sessions;
      const inFlight = submitInFlight.current;
      if (
        loading ||
        (inFlight &&
          inFlight.sessionId === sessionId &&
          inFlight.publicKey === publicKey &&
          inFlight.applicationId === applicationId) ||
        !mounted.current ||
        !applicationId ||
        activeSessions.getIn(['session', 'id']) !== sessionId ||
        activeSessions.get('public_key') !== publicKey ||
        activeSessions.getIn(['session', 'application_id']) !== applicationId
      ) {
        return undefined;
      }

      const submission = { sessionId, publicKey, applicationId };
      submitInFlight.current = submission;
      const key = idempotencyKey || newIdempotencyKey();
      if (!idempotencyKey) {
        dispatch({
          type: POST_APPROVAL_PLAID_IDEMPOTENCY_KEY_SET,
          payload: key,
        });
      }

      const options = {
        publicToken,
        accountId: account.id || account.account_id,
        idempotencyKey: key,
      };

      const clearSettledKey = () => {
        dispatch({
          type: POST_APPROVAL_PLAID_IDEMPOTENCY_KEY_SET,
          payload: null,
          expectedKey: key,
        });
      };

      return dispatch(submit(applicationId, options, { sessionId, publicKey }))
        .then((result) => {
          clearSettledKey();
          return result;
        })
        .catch((submitError) => {
          if (!isAmbiguousSubmit(submitError)) {
            clearSettledKey();
            throw submitError;
          }
          return dispatch(
            recoverSubmit(applicationId, options, {
              sessionId,
              publicKey,
              generation: submitError.generation,
            })
          )
            .then((recovered) => {
              if (recovered) clearSettledKey();
              return recovered;
            })
            .catch((recoveryError) => {
              if (!isAmbiguousSubmit(recoveryError)) clearSettledKey();
              throw recoveryError;
            });
        })
        .finally(() => {
          if (submitInFlight.current === submission) {
            submitInFlight.current = null;
          }
        });
    },
    [
      applicationId,
      dispatch,
      idempotencyKey,
      loading,
      publicKey,
      sessionId,
      store,
    ]
  );

  const skip = useCallback(
    () =>
      applicationId &&
      dispatch(skipPostApprovalPlaid(applicationId, { sessionId, publicKey })),
    [applicationId, dispatch, publicKey, sessionId]
  );

  const retryStatus = useCallback(() => {
    slowRetries.current = 0;
    consecutiveFailures.current = 0;
    lastPollError.current = null;
    setPollingExhausted(false);
    return refreshStatus();
  }, [refreshStatus]);

  const phase = useMemo(() => {
    const statusPhase = pollingPhase({
      status: attempt.get('status'),
      deadlineAt: attempt.get('deadline_at'),
      lastError: error,
    });

    if (statusPhase !== 'terminal') return statusPhase;
    if (entityRefresh.key !== terminalKey) return 'refreshing_entities';
    if (entityRefresh.status === 'ready') return 'terminal';
    if (entityRefresh.status === 'error') return 'entity_refresh_error';
    return 'refreshing_entities';
  }, [attempt, entityRefresh, error, terminalKey]);

  useEffect(() => {
    if (!active) return undefined;

    mounted.current = true;
    dispatch({ type: POST_APPROVAL_PLAID_RESET });
    pollAttempt.current = 1;
    slowRetries.current = 0;
    consecutiveFailures.current = 0;
    lastPollError.current = null;
    pollingAttemptId.current = null;
    setPollingExhausted(false);
    setEntityRefresh({ key: null, status: 'idle' });
    const request = refreshStatus();
    if (request) request.catch(() => undefined);

    return () => {
      mounted.current = false;
      dispatch({ type: POST_APPROVAL_PLAID_RESET });
    };
  }, [active, dispatch, refreshStatus, sessionId]);

  useEffect(() => {
    if (!active) return undefined;

    const attemptId = attempt.get('attempt_id');
    if (pollingAttemptId.current !== attemptId) {
      pollingAttemptId.current = attemptId;
      pollAttempt.current = 1;
      slowRetries.current = 0;
      consecutiveFailures.current = 0;
      lastPollError.current = null;
      setPollingExhausted(false);
    }

    if (loading || attempt.get('status') !== 'processing') return undefined;

    if (error) {
      if (lastPollError.current !== error) {
        consecutiveFailures.current += 1;
        lastPollError.current = error;
      }
    } else {
      if (lastPollError.current) {
        consecutiveFailures.current = 0;
        setPollingExhausted(false);
      }
      lastPollError.current = null;
    }

    if (
      !shouldAutoRetry({
        consecutiveFailures: consecutiveFailures.current,
        awaitingServerRetries: slowRetries.current,
        lastError: error,
      })
    ) {
      setPollingExhausted(true);
      return undefined;
    }

    const awaitingServer = phase === 'awaiting_server';
    const delay = awaitingServer
      ? SLOW_RETRY_DELAY_MS
      : nextDelayMs({
          attemptNumber: pollAttempt.current,
          deadlineAt: attempt.get('deadline_at'),
        });
    const timer = setTimeout(() => {
      if (awaitingServer) {
        slowRetries.current += 1;
      } else {
        pollAttempt.current += 1;
      }
      const request = refreshStatus();
      if (request) request.catch(() => undefined);
    }, delay);

    return () => clearTimeout(timer);
  }, [active, attempt, loading, phase, refreshStatus]);

  useEffect(() => {
    if (!active) return;
    if (loading || !terminalKey || entityRefresh.key === terminalKey) return;
    refreshEntities().catch(() => undefined);
  }, [active, entityRefresh.key, loading, refreshEntities, terminalKey]);

  return {
    attempt,
    hasAttempt: plaid.get('has_attempt'),
    preAttemptCanContinue: plaid.get('pre_attempt_can_continue'),
    preAttemptNextRoutePath: plaid.get('pre_attempt_next_route_path'),
    phase,
    loading,
    error,
    idempotencyKey,
    pollingExhausted,
    pollCount: pollAttempt.current + slowRetries.current,
    refreshStatus,
    openLink,
    submitSelectedAccount,
    skip,
    retryStatus,
    refreshEntities,
    entityRefreshError:
      entityRefresh.key === terminalKey ? entityRefresh.error : null,
  };
};

export default usePostApprovalPlaid;
